import type {
  InitializeMapDataMap,
  InitializeMapDestinations,
  InitializeMapMappingRule,
  InitializeMapMappingSummary,
  InitializeMapSourceField,
} from "@dashboardApi/apis/appsAppGroupIdUserImportsImportIdInitializeMap/types";
import type {
  BigQuerySegmentsConfig,
  CdiSegmentsConfig,
  DatabricksSegmentsConfig,
  RedshiftSegmentsConfig,
  SnowflakeSegmentsConfig,
  FabricSegmentsConfig,
} from "@dashboardApi/apis/integrationsDirectCdiSegmentsConfig";
import type {
  BdsDataCategory,
  PartnerName,
  SyncDestination,
} from "@technology-partners/cdi-import-sync-shared/shared/types";
import type { SyncDataType } from "@technology-partners/cdi-import-sync-shared/syncs/CdiSyncDataType";

import type { I18nT } from "@lib/dashboardI18n";
import sharedConstants from "@lib/shared_constants";

export type CdiImportStatus = "active" | "paused" | "draft";

export const syncTypeFriendlyName = (syncDataType: SyncDataType, t: I18nT<"cdi-import-sync">): string => {
  switch (syncDataType) {
    case "canvas":
      return t("sync-type-canvas", "Canvas Triggers");
    case "attributes":
      return t("sync-type-user-attributes", "User Attributes");
    case "catalog":
      return t("sync-type-catalog", "Catalog");
    case "deletes":
      return t("sync-type-delete-users", "Delete Users");
    case "events":
      return t("sync-type-custom-events", "Custom Events");
    case "purchases":
      return t("sync-type-purchase-events", "Purchase Events");
    case "accounts":
      return t("sync-type-accounts", "Accounts");
    case "bds":
      return t("sync-type-bds", "BrazeAI Decisioning Studio");
    case "data_objects":
      return t("sync-type-data-objects", "Data Objects");
    case "data_object_user_relationships":
      return t("sync-type-data-object-user-relationships", "Data Object User Relationships");
    case "data_object_object_relationships":
      return t("sync-type-data-object-relationships", "Data Object Relationships");
  }
};

export interface BaseImportSync {
  integration_id?: string;
  integration_name: string;
  integration_type: SyncDataType;
  integration_status?: CdiImportStatus;
  contact_emails: string;
  notification_preference?: NotificationPreference;
  schedule: string;
  api_key?: string;
  last_sync?: CdiDataSync;
  update_existing_only?: boolean;
  catalog_name?: string;
  data_object_type_name?: string;
  data_object_rel_kind?: string;
  data_object_related_type_name?: string;
  canvas_id?: string;
  /** Workspace custom event every row is ingested as (visual mapper event syncs). */
  event_name?: string;
  source_data_warehouse?: string;
  /** No-code mapper DataMap. Sent on save for visual_mapper syncs; returned on read for rehydration. */
  data_map?: CdiInitializeMapDataMap;
}

export interface CDISeparateSourceSync extends BaseImportSync {
  source_data_warehouse: string;
  partner_name?: PartnerName;
  destination?: SyncDestination;
  bds_data_category?: BdsDataCategory;
  composite_keys?: string[];
  big_query_table_name?: string;
  table_name?: string;
  sqs_url?: string;
  pubsub_subscription?: string;
  queue_name?: string;
  folder?: string;
  reader_behavior?: "standard" | "custom_sql" | "visual_mapper";
  sql_query?: string;
}

export interface SnowflakeImportSync extends BaseImportSync {
  data_warehouse?: SnowflakeWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name;
}

export interface RedshiftImportSync extends BaseImportSync {
  data_warehouse?: RedshiftWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.redshift.name;
}

export interface BigQueryImportSync extends BaseImportSync {
  data_warehouse?: BigQueryWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name;
}

export interface DatabricksImportSync extends BaseImportSync {
  data_warehouse?: DatabricksWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.databricks.name;
}

export interface S3ImportSync extends BaseImportSync {
  data_warehouse?: S3Warehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
}

export interface FabricImportSync extends BaseImportSync {
  data_warehouse?: FabricWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.fabric.name;
}

export interface GcsImportSync extends BaseImportSync {
  data_warehouse?: GcsWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.gcs.name;
}

export interface AzureBlobImportSync extends BaseImportSync {
  data_warehouse?: AzureBlobWarehouse;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.azure_blob.name;
}

export interface NotificationPreference {
  notification_preference_id?: string;
  contact_emails: string;
  notify_on_sync_errors?: boolean;
  notify_on_row_errors?: boolean;
  notify_on_success?: boolean;
  notify_on_empty_syncs?: boolean;
  is_active?: boolean;
  error_threshold?: number;
}

export type CdiImportSync =
  | SnowflakeImportSync
  | RedshiftImportSync
  | BigQueryImportSync
  | DatabricksImportSync
  | S3ImportSync
  | FabricImportSync
  | GcsImportSync
  | AzureBlobImportSync
  | CDISeparateSourceSync;

export type CdiErrorResponse = {
  result: "error";
  response_code: number;
  message: string;
};

export interface SnowflakeImportKey {
  id?: string;
  user: string;
  role: string;
  region?: string;
  snowflake_account: string;
  rsa_public_key?: string;
  rsa_public_key_old?: string | null;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name;
  app_group_id?: string;
}

export interface RedshiftImportKey {
  id?: string;
  user: string;
  password: string;
  host: string;
  port?: number;
  is_private_network?: boolean;
  ec2_host?: string;
  ssh_user?: string;
  ssh_port?: number;
  rsa_public_key?: string;
  rsa_public_key_old?: string | null;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.redshift.name;
  app_group_id?: string;
}

export interface BigQueryImportKey {
  id?: string;
  service_account_name: string;
  service_account_creds?: string;
  /** Null on credentials created before WIF existed; treat as `service_account_key`. */
  auth_method?: "service_account_key" | "wif" | null;
  /** Null unless the credential is `wif`. */
  project_number?: string | null;
  pool_id?: string | null;
  provider_id?: string | null;
  service_account_email?: string | null;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name;
  app_group_id?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * A Databricks credential, in either of its two auth-method shapes.
 *
 * Multi-table underneath: the shared fields live on the parent and
 * the credential material on an auth-method subclass, but the serializer
 * presents one flat payload discriminated by `auth_method`.
 *
 * The method-specific fields are optional here because exactly one method's
 * fields are populated on any given record. `auth_method` is optional for the
 * same reason it is optional server-side - it defaults to `pat`, so every
 * credential created before OAuth M2M existed omits it entirely.
 */
export interface DatabricksImportKey {
  id?: string;
  cred_name: string;
  hostname: string;
  http_path: string;
  /** Present only on a `pat` credential. */
  access_token?: string;
  /** Absent on credentials created before OAuth M2M existed; treat as `pat`. */
  auth_method?: "pat" | "oauth_m2m";
  /** Present only on an `oauth_m2m` credential. */
  client_id?: string;
  /** Present only on an `oauth_m2m` credential. */
  client_secret?: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.databricks.name;
  app_group_id?: string;
  /** Stamped by the model on save; null on rows predating the timestamps. */
  created_at?: string | null;
  updated_at?: string | null;
}

export interface S3ImportKey {
  id?: string;
  role_arn: string;
  external_id: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
  app_group_id?: string;
}

export interface FabricImportKey {
  id?: string;
  tenant_id: string;
  principal_id: string;
  client_secret: string;
  name: string;
  connection_string: string;
  azure_vm?: string;
  ssh_user?: string;
  ssh_port?: number;
  rsa_public_key?: string;
  rsa_public_key_old?: string | null;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.fabric.name;
  app_group_id?: string;
}

// GCS reuses the BigQuery credential model backend-side (same service account JSON fields).
export interface GcsImportKey {
  id?: string;
  service_account_name: string;
  service_account_creds?: string;
  /** Null on credentials created before WIF existed; treat as `service_account_key`. */
  auth_method?: "service_account_key" | "wif" | null;
  /** Null unless the credential is `wif`. */
  project_number?: string | null;
  pool_id?: string | null;
  provider_id?: string | null;
  service_account_email?: string | null;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.gcs.name;
  app_group_id?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AzureBlobImportKey {
  id?: string;
  tenant_id: string;
  principal_id: string;
  client_secret: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.azure_blob.name;
  app_group_id?: string;
}

export type CdiImportKey =
  | SnowflakeImportKey
  | RedshiftImportKey
  | BigQueryImportKey
  | DatabricksImportKey
  | S3ImportKey
  | FabricImportKey
  | GcsImportKey
  | AzureBlobImportKey;

export type SnowflakeImportKeyWithId = SnowflakeImportKey & { id: string };
export type RedshiftImportKeyWithId = RedshiftImportKey & { id: string };
export type BigQueryImportKeyWithId = BigQueryImportKey & { id: string };
export type DatabricksImportKeyWithId = DatabricksImportKey & { id: string };
export type S3ImportKeyWithId = S3ImportKey & { id: string };
export type FabricImportKeyWithId = FabricImportKey & { id: string };
export type GcsImportKeyWithId = GcsImportKey & { id: string };
export type AzureBlobImportKeyWithId = AzureBlobImportKey & { id: string };

export type CdiImportKeyWithId =
  | SnowflakeImportKeyWithId
  | RedshiftImportKeyWithId
  | BigQueryImportKeyWithId
  | DatabricksImportKeyWithId
  | S3ImportKeyWithId
  | FabricImportKeyWithId
  | GcsImportKeyWithId
  | AzureBlobImportKeyWithId;

export interface AllCdiImportKey {
  snowflake: SnowflakeImportKeyWithId[];
  redshift: RedshiftImportKeyWithId[];
  bigquery: BigQueryImportKeyWithId[];
  databricks: DatabricksImportKeyWithId[];
  s3: S3ImportKeyWithId[];
  fabric: FabricImportKeyWithId[];
  gcs: GcsImportKeyWithId[];
  azure_blob: AzureBlobImportKeyWithId[];
}

export interface SnowflakeImportKeysById {
  [key: string]: SnowflakeImportKey;
}

export interface SnowflakeWarehouse {
  id?: string;
  database: string;
  warehouse: string;
  schema: string;
  table: string;
  security_config?: SnowflakeImportKey;
  existing_creds_id?: string;
}

export interface RedshiftWarehouse {
  id?: string;
  database: string;
  schema: string;
  table: string;
  security_config?: RedshiftImportKey;
  existing_creds_id?: string;
}

export interface BigQueryWarehouse {
  id?: string;
  project: string;
  dataset: string;
  table: string;
  security_config?: BigQueryImportKey;
  existing_creds_id?: string;
}

export interface DatabricksWarehouse {
  id?: string;
  catalog: string;
  schema: string;
  table: string;
  security_config?: DatabricksImportKey;
  existing_creds_id?: string;
}

export interface S3Warehouse {
  id?: string;
  sqs_url: string;
  bucket: string;
  folder?: string;
  region: string;
  security_config?: S3ImportKey;
  existing_creds_id?: string;
}

export interface FabricWarehouse {
  id?: string;
  database: string;
  schema: string;
  table: string;
  security_config?: FabricImportKey;
  existing_creds_id?: string;
}

export interface GcsWarehouse {
  id?: string;
  pubsub_subscription: string;
  bucket: string;
  project: string;
  folder?: string;
  security_config?: GcsImportKey;
  existing_creds_id?: string;
}

export interface AzureBlobWarehouse {
  id?: string;
  queue_name: string;
  storage_account: string;
  container: string;
  folder?: string;
  security_config?: AzureBlobImportKey;
  existing_creds_id?: string;
}

export interface CdiDataSync {
  job_run_id: string;
  integration_id: string;
  job_status: string;
  job_start_time: string;
  job_finish_time?: string;
  errors?: string | null;
  rows_read?: number;
  rows_ingested?: number;
  rows_rejected_in_read?: number;
  rows_rejected_in_ingest?: number;
  sync_data_type: SyncDataType | null;
  s3_file_path?: string;
  finish_sync_timestamp?: string;
  s3_sync_input_files?: string[];
}

export interface CdiImportSyncsResponse {
  import_syncs: CdiImportSync[];
}

export interface CdiImportKeysResponse {
  import_keys: CdiImportKeyWithId[];
}

export interface CreateRedshiftImportSyncParams {
  appGroupId: string;
  importSync: RedshiftImportSync;
}

export interface CreateSnowflakeImportSyncParams {
  appGroupId: string;
  importSync: SnowflakeImportSync;
}

export interface CreateCdiImportSyncParams {
  appGroupId: string;
  importSync: CdiImportSync;
}

export interface DeleteCdiImportSyncParams {
  syncId: string;
  partnerName: PartnerName;
}

export interface UpdateSnowflakeImportSyncStatusParams {
  syncId: string;
  status: "active" | "paused";
  partnerName: PartnerName;
}

export interface ListCdiImportSyncsParams {
  appGroupId: string;
  partnerName: PartnerName;
}

export interface GetCdiImportSyncsParams {
  syncId: string;
}

export type BdsDestinationSchemaStatus =
  | "dataset_not_created"
  | "table_not_created"
  | "compared"
  | "skipped"
  | "unavailable";

export interface BdsDestinationSchemaColumnRef {
  name: string;
  is_primary_key?: boolean;
  source_type?: string | null;
  destination_type?: string | null;
}

export interface BdsDestinationSchemaCheck {
  status: BdsDestinationSchemaStatus;
  big_query_project?: string;
  big_query_dataset?: string;
  big_query_table_name?: string;
  warnings?: string[];
  errors?: string[];
  columns_missing_in_destination?: BdsDestinationSchemaColumnRef[];
  columns_missing_in_source?: BdsDestinationSchemaColumnRef[];
}

export interface CdiTestConnectionResponse {
  data_preview: {
    PAYLOAD?: string;
    EXTERNAL_ID?: string;
    ALIAS_NAME?: string;
    ALIAS_LABEL?: string;
    BRAZE_ID?: string;
  }[];
  error?: string;
  status: string;
  destination_schema?: BdsDestinationSchemaCheck;
}

export interface CatalogFields {
  fields: CatalogField[];
}

export interface CatalogField {
  name: string;
  type: string;
  selected?: boolean;
}

export interface CdiKeyRegenerateResult {
  status: string;
}

export interface CdiKeyRegenerateProps {
  keyId: string;
  partnerName: PartnerName;
}

export interface PostSnowflakeImportKeyDeleteProps {
  /** The key to be deleted. */
  keyId: string;
  partnerName: PartnerName;
}

export interface GetSnowflakeImportSyncHistoryProps {
  syncId: string;
  page: number;
  hide_empty: boolean;
}

export interface CdiSyncHistoryResponse {
  num_pages: number;
  results: CdiDataSync[];
}

export interface UpdateCdiImportKeyProps {
  keyId: string;
  partner_name: PartnerName;
  import_key: CdiImportKey;
  key_file?: File;
}

export interface UpdateCdiImportSyncStatusResponse {
  status: "successful" | "conflict";
  message?: string;
}

export function isSnowflakeImportSync(obj: CdiImportSync): obj is SnowflakeImportSync {
  return (
    obj !== undefined &&
    (<SnowflakeImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name &&
    (<SnowflakeImportSync>obj).data_warehouse !== undefined
  );
}

export function isRedshiftImportSync(obj: CdiImportSync): obj is RedshiftImportSync {
  return (
    obj !== undefined &&
    (<RedshiftImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name &&
    (<RedshiftImportSync>obj).data_warehouse !== undefined
  );
}

export function isBigQueryImportSync(obj: CdiImportSync): obj is BigQueryImportSync {
  return (
    obj !== undefined &&
    (<BigQueryImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name &&
    (<BigQueryImportSync>obj).data_warehouse !== undefined
  );
}

export function isDatabricksImportSync(obj: CdiImportSync): obj is DatabricksImportSync {
  return (
    obj !== undefined &&
    (<DatabricksImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name &&
    (<DatabricksImportSync>obj).data_warehouse !== undefined
  );
}

export function isS3ImportSync(obj: CdiImportSync): obj is S3ImportSync {
  return (
    obj != null &&
    (<S3ImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.s3.name &&
    (<S3ImportSync>obj).data_warehouse !== undefined
  );
}

export function isFabricImportSync(obj: CdiImportSync): obj is FabricImportSync {
  return (
    obj !== undefined &&
    (<FabricImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name &&
    (<FabricImportSync>obj).data_warehouse !== undefined
  );
}

export function isGcsImportSync(obj: CdiImportSync): obj is GcsImportSync {
  return (
    obj != null &&
    (<GcsImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.gcs.name &&
    (<GcsImportSync>obj).data_warehouse !== undefined
  );
}

export function isAzureBlobImportSync(obj: CdiImportSync): obj is AzureBlobImportSync {
  return (
    obj != null &&
    (<AzureBlobImportSync>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.azure_blob.name &&
    (<AzureBlobImportSync>obj).data_warehouse !== undefined
  );
}

export function isCDISeparateSourceSync(obj: CdiImportSync): obj is CDISeparateSourceSync {
  return obj != null && (<CDISeparateSourceSync>obj).source_data_warehouse !== undefined;
}

export function isCdiImportSync(obj: unknown): obj is CdiImportSync {
  const candidate = obj as CdiImportSync;
  return (
    isSnowflakeImportSync(candidate) ||
    isRedshiftImportSync(candidate) ||
    isBigQueryImportSync(candidate) ||
    isDatabricksImportSync(candidate) ||
    isS3ImportSync(candidate) ||
    isFabricImportSync(candidate) ||
    isGcsImportSync(candidate) ||
    isAzureBlobImportSync(candidate) ||
    isCDISeparateSourceSync(candidate)
  );
}

export function isSnowflakeSegmentsConfig(obj: CdiSegmentsConfig): obj is SnowflakeSegmentsConfig {
  return (
    obj !== undefined &&
    (<SnowflakeSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name &&
    (<SnowflakeSegmentsConfig>obj).security_config !== undefined
  );
}

export function isRedshiftSegmentsConfig(obj: CdiSegmentsConfig): obj is RedshiftSegmentsConfig {
  return (
    obj !== undefined &&
    (<RedshiftSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name &&
    (<RedshiftSegmentsConfig>obj).security_config !== undefined
  );
}

export function isBigQuerySegmentsConfig(obj: CdiSegmentsConfig): obj is BigQuerySegmentsConfig {
  return (
    obj !== undefined &&
    (<BigQuerySegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name &&
    (<BigQuerySegmentsConfig>obj).security_config !== undefined
  );
}

export function isDatabricksSegmentsConfig(obj: CdiSegmentsConfig): obj is DatabricksSegmentsConfig {
  return (
    obj !== undefined &&
    (<DatabricksSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name &&
    (<DatabricksSegmentsConfig>obj).security_config !== undefined
  );
}

export function isFabricSegmentsConfig(obj: CdiSegmentsConfig): obj is FabricSegmentsConfig {
  return (
    obj !== undefined &&
    (<FabricSegmentsConfig>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name &&
    (<FabricSegmentsConfig>obj).security_config !== undefined
  );
}

export function isCdiSegmentsConfig(obj: unknown): obj is CdiSegmentsConfig {
  const candidate = obj as CdiSegmentsConfig;
  return (
    isSnowflakeSegmentsConfig(candidate) ||
    isRedshiftSegmentsConfig(candidate) ||
    isBigQuerySegmentsConfig(candidate) ||
    isDatabricksSegmentsConfig(candidate) ||
    isFabricSegmentsConfig(candidate)
  );
}

export function isCdiErrorResponse(obj: unknown): obj is CdiErrorResponse {
  const candidate = obj as CdiErrorResponse | undefined;
  return (
    Boolean(obj) &&
    candidate?.result === "error" &&
    typeof candidate?.response_code == "number" &&
    typeof candidate?.message === "string"
  );
}

// CDI_SOURCE_EXTENSION: file-vs-warehouse classification is driven by each source's `source_type` in
// sharedConstants. A new file/"reactive" source flows through this predicate automatically once its
// `source_type` is "file", but revisit the file-source branches that call this (sync config, scheduling,
// data selection, job summary) since several still branch on `partner_name === "s3"` directly.
export function isFileStoreIntegration(obj: CdiImportSync | undefined): boolean {
  return !!obj?.partner_name && sharedConstants.DATALAKE.PARTNER_NAME[obj.partner_name]?.source_type === "file";
}

/**
 * Synthetic mapping rule for visual mapper custom event syncs. Every ingested row becomes this
 * workspace event; Kirby reads `destination_field` (the dropdown selection). Not a source column.
 * `source_path`/`source_value` pin the sync to one value of an event-name column, which Kirby
 * compiles to a filtering WHERE clause; the column is never projected, and one key alone is rejected.
 */
export interface CdiEventNameMappingRule {
  mapping_kind: "event_name";
  destination_field: string;
  source_path?: string;
  source_value?: string;
}

export type CdiInitializeMapMappingRule = InitializeMapMappingRule | CdiEventNameMappingRule;
export type CdiInitializeMapDataMap = Omit<InitializeMapDataMap, "mapping_rules"> & {
  mapping_rules: CdiInitializeMapMappingRule[];
};
export type CdiInitializeMapDestinations = InitializeMapDestinations;
export type CdiInitializeMapMappingSummary = InitializeMapMappingSummary;
export type CdiInitializeMapSourceField = InitializeMapSourceField;

/**
 * A saved mapping rule whose source column no longer exists in the live table. CDI-only: it is
 * derived from `persisted_rules`, which only the edit flow sends, so it is always empty on create.
 */
export interface CdiRemovedSourceColumn {
  /** Absent on legacy persisted rules that were matched by index alone. */
  source_path: string | null;
  source_index: number | null;
  destination_field: string;
  is_identifier: boolean;
}

export interface PostInitializeCdiMapProps {
  app_group_id: string;
  source_id: string;
  table_name: string;
  integration_type: string;
  catalog_name?: string;
  /** `api_workflow_id` of the destination canvas. Required when `integration_type` is `canvas`. */
  canvas_id?: string;
  /** Workspace custom event every row is ingested as. Used when `integration_type` is `events`. */
  event_name?: string;
  persisted_rules?: InitializeMapMappingRule[];
}

export interface PostInitializeCdiMapResult {
  data_map: InitializeMapDataMap;
  source: InitializeMapSourceField[];
  destinations: CdiInitializeMapDestinations;
  mapping_summary: InitializeMapMappingSummary;
  removed_source_columns?: CdiRemovedSourceColumn[];
}
